/* eslint-disable @atlassian/relay/unused-fields */
/** @jsx jsx */
import { useMemo } from 'react';
import { css, jsx } from '@compiled/react';
import noop from 'lodash/noop';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { useLazyLoadQuery, type PreloadedQuery, graphql } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import Suspense from '@atlassian/jira-placeholder/src/index.tsx';

import {
	SuggestRelatedWorkItemsContentContainerOld,
	SuggestRelatedWorkItemsContentContainerNew,
} from '@atlassian/jira-ai-related-issues/src/AiRelatedIssuesResourcesEntrypoint.tsx';
import type {
	FormFieldValues,
	GeneralFieldValue,
} from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import type { RelatedIssuesSearchPayload } from '@atlassian/jira-ai-related-issues/src/services/use-fetch-related-issues/index.tsx';

import { expVal } from '@atlassian/jira-feature-experiments';
import {
	type RelatedIssue,
	JiraIssueRelatedIssuesContainer,
} from '@atlassian/jira-ai-related-issues/src/controllers/context.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type {
	AiRelatedIssuesResourcesEntrypointRelatedIssuesQuery as SimilarWorkItemsGicQueryType,
	ConvoAiJiraSimilarWorkItemsInput,
} from '@atlassian/jira-ai-similar-work-items/src/__generated__/AiRelatedIssuesResourcesEntrypointRelatedIssuesQuery.graphql';
import type { AiRelatedIssuesResourcesEntrypointIssueLinkTypesQuery } from '@atlassian/jira-ai-related-issues/src/__generated__/AiRelatedIssuesResourcesEntrypointIssueLinkTypesQuery.graphql';
import { useSimilarWorkItemsCommon } from './utils/use-similar-work-items-common.tsx';
import messages from './messages.tsx';

// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
const issueCreateSimilarWorkItemsQuery = graphql`
	query issueCreateSimilarWorkItemsQuery(
		$cloudId: ID!
		$experience: String!
		$workItemInput: ConvoAiJiraSimilarWorkItemsInput!
	) {
		convoai_jiraSimilarWorkItems(
			cloudId: $cloudId
			experience: $experience
			workItemInput: $workItemInput
		) {
			edges {
				node {
					id
					workItem {
						issueId
						webUrl
						summary
						key
						issueTypeAvatarUrl
					}
				}
			}
		}
	}
`;

type Props = {
	getFormState: () => FormFieldValues;
	setFieldValue: (name: string, value: GeneralFieldValue) => void;
	selectedProjectId?: string;
	isLoading?: boolean;
};

const getCustomRelatedIssueSearchPayload = (selectedProjectId: string, summary: string) => {
	return {
		project_id: selectedProjectId,
		issue_summary: summary,
	};
};

// Common component for rendering the UI
const SimilarWorkItemsUI = ({
	linkedWorkItemValues,
	isLinkedWorkItemFieldPresent,
	customRelatedIssueSearchPayload,
	onCopyButtonClick,
	onLinkIssueButtonClick,
	aiRelatedIssuesIssueLinkTypesPreloadedQuery,
	isLoading,
}: {
	linkedWorkItemValues: string[];
	isLinkedWorkItemFieldPresent: unknown;
	customRelatedIssueSearchPayload: RelatedIssuesSearchPayload;
	onCopyButtonClick: (issueKey: string) => Promise<void>;
	onLinkIssueButtonClick: (relatedIssue: RelatedIssue) => void;
	isLoading?: boolean;
	aiRelatedIssuesIssueLinkTypesPreloadedQuery?: PreloadedQuery<
		AiRelatedIssuesResourcesEntrypointIssueLinkTypesQuery,
		Record<string, unknown>
	> | null;
}) => {
	const contentContainerNew =
		aiRelatedIssuesIssueLinkTypesPreloadedQuery && fg('remove-issue-link-types-dfo') ? (
			<SuggestRelatedWorkItemsContentContainerNew
				isLoading={isLoading}
				issueKey=""
				onIssueLinkSave={noop}
				existingIssues={
					fg('similar_work_items_in_gic_link_work_items') ? linkedWorkItemValues : ['']
				}
				customRelatedIssueSearchPayload={customRelatedIssueSearchPayload}
				invokedFromSource="globalIssueCreate"
				headerTitle={messages.similarWorkItems}
				{...(expVal('similar_work_items_copy_field_milestone-2', 'isEnabled', false)
					? { onCopyButtonClick }
					: undefined)}
				{...(fg('similar_work_items_in_gic_link_work_items')
					? {
							onSimilarIssueLinkGICButtonClick: isLinkedWorkItemFieldPresent
								? onLinkIssueButtonClick
								: null,
						}
					: {})}
				aiRelatedIssuesIssueLinkTypesPreloadedQuery={aiRelatedIssuesIssueLinkTypesPreloadedQuery}
			/>
		) : null;

	const contentContainer = fg('remove-issue-link-types-dfo') ? (
		contentContainerNew
	) : (
		<SuggestRelatedWorkItemsContentContainerOld
			issueKey=""
			onIssueLinkSave={noop}
			existingIssues={fg('similar_work_items_in_gic_link_work_items') ? linkedWorkItemValues : ['']}
			customRelatedIssueSearchPayload={customRelatedIssueSearchPayload}
			invokedFromSource="globalIssueCreate"
			headerTitle={messages.similarWorkItems}
			{...(expVal('similar_work_items_copy_field_milestone-2', 'isEnabled', false)
				? { onCopyButtonClick }
				: undefined)}
			{...(fg('similar_work_items_in_gic_link_work_items')
				? {
						onSimilarIssueLinkGICButtonClick: isLinkedWorkItemFieldPresent
							? onLinkIssueButtonClick
							: null,
					}
				: {})}
		/>
	);

	return fg('similar_items_in_gic_ui_fixes') ? (
		<div css={similarWorkItemsWrapperStyles}>
			<Box xcss={relatedIssuesContainerStylesUIFix}>
				<JiraIssueRelatedIssuesContainer scope="related-issues-in-gic">
					{contentContainer}
				</JiraIssueRelatedIssuesContainer>
			</Box>
		</div>
	) : (
		<Box xcss={relatedIssuesContainerStyles}>
			<JiraIssueRelatedIssuesContainer scope="related-issues-in-gic">
				{contentContainer}
			</JiraIssueRelatedIssuesContainer>
		</Box>
	);
};

// Inner component that uses useLazyLoadQuery with network-only
const SimilarWorkItemsQueryComponent = ({
	stableVariables,
	linkedWorkItemValues,
	selectedProjectId,
	isLinkedWorkItemFieldPresent,
	debouncedSummary,
	isPending,
	aiRelatedIssuesIssueLinkTypesPreloadedQuery,
	onCopyButtonClick,
	onLinkIssueButtonClick,
	isLoading,
}: {
	stableVariables: {
		cloudId: string;
		experience: string;
		workItemInput: ConvoAiJiraSimilarWorkItemsInput;
	};
	linkedWorkItemValues: string[];
	selectedProjectId: string;
	isLinkedWorkItemFieldPresent: unknown;
	debouncedSummary: string;
	isPending: boolean;
	aiRelatedIssuesIssueLinkTypesPreloadedQuery?: PreloadedQuery<
		AiRelatedIssuesResourcesEntrypointIssueLinkTypesQuery,
		Record<string, unknown>
	> | null;
	onCopyButtonClick: (issueKey: string) => Promise<void>;
	onLinkIssueButtonClick: (relatedIssue: RelatedIssue) => void;
	isLoading?: boolean;
}) => {
	const data = useLazyLoadQuery<SimilarWorkItemsGicQueryType>(
		issueCreateSimilarWorkItemsQuery,
		stableVariables,
		{
			fetchPolicy: 'network-only',
		},
	);

	// Transform GraphQL response to RelatedIssue format
	const aggData = useMemo(() => {
		return (
			data?.convoai_jiraSimilarWorkItems?.edges
				?.map((edge) => {
					const workItem = edge?.node?.workItem;
					if (!workItem?.issueId || !workItem?.key || !workItem?.webUrl) return null;
					return {
						id: workItem.issueId,
						issueKey: workItem.key,
						issueLink: workItem.webUrl,
						issueTypeIconUrl: workItem.issueTypeAvatarUrl || '',
						issueSummary: workItem.summary || '',
					};
				})
				.filter((item): item is NonNullable<typeof item> => item !== null) ?? []
		);
	}, [data?.convoai_jiraSimilarWorkItems?.edges]);

	const customRelatedIssueSearchPayload = useMemo(() => {
		return {
			project_id: selectedProjectId,
			issue_summary: debouncedSummary,
			isAggData: true,
			_aggData: aggData,
			_loading: isPending,
			_error: null,
			// Skip analytics on remount when data is loaded to prevent duplicate events
			_skipAiInteractionAnalytics: true,
		};
	}, [selectedProjectId, debouncedSummary, aggData, isPending]);

	return (
		<SimilarWorkItemsUI
			linkedWorkItemValues={linkedWorkItemValues}
			isLinkedWorkItemFieldPresent={isLinkedWorkItemFieldPresent}
			customRelatedIssueSearchPayload={customRelatedIssueSearchPayload}
			onCopyButtonClick={onCopyButtonClick}
			onLinkIssueButtonClick={onLinkIssueButtonClick}
			aiRelatedIssuesIssueLinkTypesPreloadedQuery={aiRelatedIssuesIssueLinkTypesPreloadedQuery}
			isLoading={isLoading}
		/>
	);
};

// AGG implementation
const SimilarWorkItemInGICWithAGG = (props: Props) => {
	const commonHooks = useSimilarWorkItemsCommon(props);
	const {
		linkedWorkItemValues,
		selectedProjectId,
		isLinkedWorkItemFieldPresent,
		showComponent,
		debouncedSummary,
		isAdminHubAiEnabled,
		summary,
		aiRelatedIssuesIssueLinkTypesPreloadedQuery,
		onCopyButtonClick,
		onLinkIssueButtonClick,
		isPending,
	} = commonHooks;

	const cloudId = useCloudId();

	const shouldFetch = debouncedSummary && selectedProjectId && cloudId;

	const stableVariables = useMemo(() => {
		const workItemInput: ConvoAiJiraSimilarWorkItemsInput = {
			summary: debouncedSummary,
			projectId: selectedProjectId,
			excludeWorkItemIds: linkedWorkItemValues.length > 0 ? linkedWorkItemValues : undefined,
			similarityConfig: {
				minimumSimScore: 0.5,
			},
		};

		return {
			cloudId: cloudId || '',
			experience: 'gic',
			workItemInput,
		};
	}, [debouncedSummary, selectedProjectId, linkedWorkItemValues, cloudId]);

	if (!summary || !showComponent || !isAdminHubAiEnabled) {
		return null;
	}

	if (!shouldFetch || isPending) {
		const customRelatedIssueSearchPayload = {
			project_id: selectedProjectId,
			issue_summary: debouncedSummary,
			isAggData: true,
			_aggData: [],
			_loading: true,
			_error: null,
			_skipAiResultAnalytics: true,
		};

		return (
			<SimilarWorkItemsUI
				linkedWorkItemValues={linkedWorkItemValues}
				isLinkedWorkItemFieldPresent={isLinkedWorkItemFieldPresent}
				customRelatedIssueSearchPayload={customRelatedIssueSearchPayload}
				onCopyButtonClick={onCopyButtonClick}
				onLinkIssueButtonClick={onLinkIssueButtonClick}
				aiRelatedIssuesIssueLinkTypesPreloadedQuery={aiRelatedIssuesIssueLinkTypesPreloadedQuery}
				isLoading
			/>
		);
	}

	return (
		<SimilarWorkItemsQueryComponent
			stableVariables={stableVariables}
			linkedWorkItemValues={linkedWorkItemValues}
			selectedProjectId={selectedProjectId}
			isLinkedWorkItemFieldPresent={isLinkedWorkItemFieldPresent}
			debouncedSummary={debouncedSummary}
			isPending={isPending ?? false}
			aiRelatedIssuesIssueLinkTypesPreloadedQuery={aiRelatedIssuesIssueLinkTypesPreloadedQuery}
			onCopyButtonClick={onCopyButtonClick}
			onLinkIssueButtonClick={onLinkIssueButtonClick}
			isLoading={isPending}
		/>
	);
};

// REST implementation
const SimilarWorkItemInGICWithREST = (props: Props) => {
	const commonHooks = useSimilarWorkItemsCommon(props);
	const {
		linkedWorkItemValues,
		selectedProjectId,
		isLinkedWorkItemFieldPresent,
		showComponent,
		debouncedSummary,
		isAdminHubAiEnabled,
		aiRelatedIssuesIssueLinkTypesPreloadedQuery,
		onCopyButtonClick,
		onLinkIssueButtonClick,
	} = commonHooks;

	const customRelatedIssueSearchPayload = useMemo(() => {
		return getCustomRelatedIssueSearchPayload(selectedProjectId, debouncedSummary);
	}, [selectedProjectId, debouncedSummary]);

	if (!showComponent || !isAdminHubAiEnabled) {
		return null;
	}

	return (
		<SimilarWorkItemsUI
			linkedWorkItemValues={linkedWorkItemValues}
			isLinkedWorkItemFieldPresent={isLinkedWorkItemFieldPresent}
			customRelatedIssueSearchPayload={customRelatedIssueSearchPayload}
			onCopyButtonClick={onCopyButtonClick}
			onLinkIssueButtonClick={onLinkIssueButtonClick}
			aiRelatedIssuesIssueLinkTypesPreloadedQuery={aiRelatedIssuesIssueLinkTypesPreloadedQuery}
		/>
	);
};

export const SimilarWorkItemInGIC = (props: Props) => {
	if (fg('thor_gic_similar_issues_relay_migration')) {
		return (
			<Suspense name="SimilarWorkItemInGIC" fallback={null}>
				<SimilarWorkItemInGICWithAGG {...props} />
			</Suspense>
		);
	}

	return <SimilarWorkItemInGICWithREST {...props} />;
};

const relatedIssuesContainerStylesUIFix = xcss({
	marginTop: 'space.050',
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border.input',
	borderRadius: 'radius.small',
});

const relatedIssuesContainerStyles = xcss({
	marginTop: 'space.300',
});

const similarWorkItemsWrapperStyles = css({
	zIndex: 0,
	position: 'relative',
});
